import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Autocomplete,
  Tooltip,
  TablePagination,
  Rating,
  Stack,
  Avatar,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  FilterAltOff,
  Star as StarIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ThumbUp as ThumbUpIcon,
} from '@mui/icons-material';
import {
  contactGoogleRatingAPI,
  contactsAPI,
  citiesAPI,
  categoriesAPI,
} from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';

const ContactGoogleRating = () => {
  const { t } = useLanguage();

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingContacts, setIsFetchingContacts] = useState(false);
  const [isFetchingCategories, setIsFetchingCategories] = useState(false);

  // Main Data
  const [ratingsList, setRatingsList] = useState([]);
  const [allContactsList, setAllContactsList] = useState([]); // All contacts cached for table title lookup

  // Dropdown Filter options for Add/Edit Modal
  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modalContacts, setModalContacts] = useState([]);

  // Selections for Dialog form cascade
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [formSelectedContact, setFormSelectedContact] = useState(null);

  // Table Filters & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Delete Dialog State
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    contactId: null,
    rating: 5,
    totalRatings: 0,
    reviewUrl: '',
    isActive: true,
  });

  const [formErrors, setFormErrors] = useState({
    contactId: '',
    rating: '',
    totalRatings: '',
    reviewUrl: '',
  });

  // Snackbar Toast
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* ------------------- Initial Load: Fetch Cities & All Contacts ------------------- */
  const fetchCities = useCallback(async () => {
    try {
      const res = await citiesAPI.getCities();
      if (res?.data?.success && Array.isArray(res.data.payload)) {
        const list = res.data.payload;
        setCities(list);
        if (list.length > 0 && !selectedCityId) {
          setSelectedCityId(list[0].id);
        }
      }
    } catch (e) {
      console.error('Error fetching cities:', e);
    }
  }, [selectedCityId]);

  const fetchAllContactsForLookup = useCallback(async () => {
    try {
      const res = await contactsAPI.getContacts(1, null, 0, 1000);
      let list = [];
      if (res?.data?.payload && Array.isArray(res.data.payload)) {
        list = res.data.payload;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
      }
      setAllContactsList((prev) => {
        const existingIds = new Set(prev.map((c) => String(c.id)));
        const newItems = list.filter((c) => !existingIds.has(String(c.id)));
        return [...prev, ...newItems];
      });
    } catch (err) {
      console.error('Error fetching all contacts for lookup:', err);
    }
  }, []);

  useEffect(() => {
    fetchCities();
    fetchAllContactsForLookup();
  }, [fetchCities, fetchAllContactsForLookup]);

  /* ------------------- Load Categories for Selected City ------------------- */
  const fetchCategoriesForCity = useCallback(async (cityId) => {
    if (!cityId) return;
    setIsFetchingCategories(true);
    try {
      const res = await categoriesAPI.getCategories(cityId, 0, 100, { hasContact: true });
      if (res?.data?.success && Array.isArray(res.data.payload)) {
        setCategories(res.data.payload);
      } else if (Array.isArray(res?.data)) {
        setCategories(res.data);
      } else {
        setCategories([]);
      }
    } catch (e) {
      console.error('Error fetching categories for city:', e);
      setCategories([]);
    } finally {
      setIsFetchingCategories(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCityId) {
      fetchCategoriesForCity(selectedCityId);
    }
  }, [selectedCityId, fetchCategoriesForCity]);

  /* ------------------- Load Contacts for Selected City & Category ------------------- */
  const fetchContactsForModal = useCallback(async (cityId, categoryId = '') => {
    setIsFetchingContacts(true);
    try {
      const currentCity = cityId || (cities[0]?.id ?? 1);
      const res = await contactsAPI.getContacts(currentCity, categoryId || null, 0, 100);
      let list = [];
      if (res?.data?.payload && Array.isArray(res.data.payload)) {
        list = res.data.payload;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
      }
      setModalContacts(list);
      return list;
    } catch (err) {
      console.error('Error fetching modal contacts:', err);
      setModalContacts([]);
      return [];
    } finally {
      setIsFetchingContacts(false);
    }
  }, [cities]);

  useEffect(() => {
    if (openDialog && selectedCityId) {
      fetchContactsForModal(selectedCityId, selectedCategoryId);
    }
  }, [openDialog, selectedCityId, selectedCategoryId, fetchContactsForModal]);

  /* ------------------- Fetch Google Ratings List ------------------- */
  const fetchRatings = useCallback(async () => {
    setIsLoading(true);
    try {
      const options = {};
      if (statusFilter === 'active') options.isActive = true;
      if (statusFilter === 'inactive') options.isActive = false;

      const response = await contactGoogleRatingAPI.getContactGoogleRating(null, options);

      let data = [];
      if (response?.data?.payload && Array.isArray(response.data.payload)) {
        data = response.data.payload;
      } else if (Array.isArray(response?.data)) {
        data = response.data;
      } else if (response?.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      }

      setRatingsList(data);
    } catch (err) {
      console.error('Error fetching contact google ratings:', err);
      showSnackbar(t('contactGoogleRating.toastFetchError') || 'Failed to fetch Google ratings data.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchRatings();
  }, [fetchRatings]);

  // Auto-fetch missing contact details by contactId for ratings list
  useEffect(() => {
    if (!ratingsList || ratingsList.length === 0) return;
    const missingIds = ratingsList
      .map((r) => r.contactId)
      .filter((id) => id != null && id !== '' && !allContactsList.some((c) => String(c.id) === String(id)));

    if (missingIds.length > 0) {
      Promise.allSettled(
        missingIds.map((id) => contactsAPI.getContact(id))
      ).then((results) => {
        const fetchedContacts = results
          .filter((r) => r.status === 'fulfilled' && r.value?.data?.payload)
          .map((r) => r.value.data.payload);

        if (fetchedContacts.length > 0) {
          setAllContactsList((prev) => {
            const existingIds = new Set(prev.map((c) => String(c.id)));
            const newItems = fetchedContacts.filter((c) => !existingIds.has(String(c.id)));
            return [...prev, ...newItems];
          });
        }
      });
    }
  }, [ratingsList, allContactsList]);

  // Lookup helper to get contact name by contactId
  const getContactTitle = useCallback(
    (contactId, row = null) => {
      if (row) {
        const directTitle = row.contactTitle || row.title || row.contactName || row.name || row.entityName;
        if (directTitle && String(directTitle).trim()) return String(directTitle).trim();
      }
      const found = allContactsList.find((c) => String(c.id) === String(contactId) || String(c.contactId) === String(contactId)) ||
                    modalContacts.find((c) => String(c.id) === String(contactId) || String(c.contactId) === String(contactId));
      if (found) {
        const title = found.title || found.title_EN || found.name || found.contactPerson || found.contactTitle || found.title_DE;
        if (title && String(title).trim()) return String(title).trim();
      }
      return `Contact #${contactId}`;
    },
    [allContactsList, modalContacts]
  );

  // Filtered ratings list computation
  const filteredRatings = useMemo(() => {
    return ratingsList.filter((item) => {
      // Status Filter
      if (statusFilter === 'active' && !item.isActive) return false;
      if (statusFilter === 'inactive' && item.isActive) return false;

      // Search Filter (by contact title, contactId, reviewUrl)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const contactTitle = getContactTitle(item.contactId).toLowerCase();
        const idMatch = String(item.contactId || '').includes(query);
        const titleMatch = contactTitle.includes(query);
        const urlMatch = String(item.reviewUrl || '').toLowerCase().includes(query);

        if (!idMatch && !titleMatch && !urlMatch) return false;
      }

      return true;
    });
  }, [ratingsList, statusFilter, searchQuery, getContactTitle]);

  // Pagination Change handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open Dialog for Add
  const handleOpenAdd = () => {
    setIsEditMode(false);
    const initialCity = cities.length > 0 ? cities[0].id : '';
    setSelectedCityId(initialCity);
    setSelectedCategoryId('');
    setFormSelectedContact(null);
    setFormData({
      contactId: null,
      rating: 5,
      totalRatings: 0,
      reviewUrl: '',
      isActive: true,
    });
    setFormErrors({});
    if (initialCity) {
      fetchCategoriesForCity(initialCity);
      fetchContactsForModal(initialCity, '');
    }
    setOpenDialog(true);
  };

  // Open Dialog for Edit
  const handleOpenEdit = async (item) => {
    setIsEditMode(true);

    const foundContact = allContactsList.find((c) => String(c.id) === String(item.contactId)) || {
      id: item.contactId,
      title: getContactTitle(item.contactId),
    };

    const targetCityId = foundContact.cityId || (cities.length > 0 ? cities[0].id : '');
    const targetCatId = foundContact.categoryId || '';

    setSelectedCityId(targetCityId);
    setSelectedCategoryId(targetCatId);

    if (targetCityId) {
      fetchCategoriesForCity(targetCityId);
      fetchContactsForModal(targetCityId, targetCatId);
    }

    setFormSelectedContact(foundContact);
    setFormData({
      contactId: item.contactId,
      rating: item.rating ?? 5,
      totalRatings: item.totalRatings ?? 0,
      reviewUrl: item.reviewUrl || '',
      isActive: item.isActive ?? true,
    });
    setFormErrors({});
    setOpenDialog(true);
  };

  // Form Validation
  const validateForm = () => {
    const errors = {};
    if (!formData.contactId) {
      errors.contactId = t('contactGoogleRating.errContactRequired') || 'Please select a contact.';
    }
    if (formData.rating === null || formData.rating === undefined || formData.rating < 0 || formData.rating > 5) {
      errors.rating = t('contactGoogleRating.errRatingInvalid') || 'Rating must be between 0 and 5.';
    }
    if (formData.totalRatings === null || formData.totalRatings === undefined || formData.totalRatings < 0) {
      errors.totalRatings = t('contactGoogleRating.errTotalRatingsInvalid') || 'Total ratings must be 0 or greater.';
    }
    if (formData.reviewUrl && !/^https?:\/\/.+/i.test(formData.reviewUrl.trim())) {
      errors.reviewUrl = t('contactGoogleRating.errUrlInvalid') || 'Please enter a valid URL starting with http:// or https://';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Save (Create / Update)
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const payload = {
        contactId: Number(formData.contactId),
        rating: Number(formData.rating),
        totalRatings: Number(formData.totalRatings),
        reviewUrl: formData.reviewUrl ? formData.reviewUrl.trim() : '',
        isActive: Boolean(formData.isActive),
      };

      let res;
      if (isEditMode) {
        res = await contactGoogleRatingAPI.updateContactGoogleRating(payload);
      } else {
        res = await contactGoogleRatingAPI.createContactGoogleRating(payload);
      }

      if (res?.status === 200 || res?.status === 201 || res?.data?.success) {
        showSnackbar(
          isEditMode
            ? (t('contactGoogleRating.toastUpdated') || 'Google rating updated successfully.')
            : (t('contactGoogleRating.toastCreated') || 'Google rating created successfully.'),
          'success'
        );
        setOpenDialog(false);
        fetchRatings();
        fetchAllContactsForLookup();
      } else {
        showSnackbar(res?.data?.message || t('contactGoogleRating.toastError') || 'Failed to save Google rating.', 'error');
      }
    } catch (err) {
      console.error('Error saving google rating:', err);
      showSnackbar(err?.response?.data?.message || t('contactGoogleRating.toastError') || 'Error saving Google rating.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete Action
  const handleOpenDelete = (item) => {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsSubmitting(true);
    try {
      const res = await contactGoogleRatingAPI.deleteContactGoogleRating(itemToDelete.contactId);
      if (res?.status === 200 || res?.data?.success) {
        showSnackbar(t('contactGoogleRating.toastDeleted') || 'Google rating deleted successfully.', 'success');
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        fetchRatings();
      } else {
        showSnackbar(res?.data?.message || t('contactGoogleRating.toastDeleteError') || 'Failed to delete Google rating.', 'error');
      }
    } catch (err) {
      console.error('Error deleting google rating:', err);
      showSnackbar(err?.response?.data?.message || t('contactGoogleRating.toastDeleteError') || 'Error deleting Google rating.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setPage(0);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      {/* Screen Title Block */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
            {t('nav.contactGoogleRating') || 'Google Ratings'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#6B7280', mt: 0.5 }}>
            {t('contactGoogleRating.subtitle') || 'Manage Google ratings and reviews for contacts.'}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            onClick={fetchRatings}
            disabled={isLoading}
            startIcon={<RefreshIcon />}
            sx={{
              borderColor: '#328284',
              color: '#328284',
              height: 40,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: 'rgba(50, 130, 132, 0.08)', borderColor: '#0F3D3E' },
            }}
          >
            {t('common.refresh') || 'Refresh'}
          </Button>

          <Button
            variant="contained"
            onClick={handleOpenAdd}
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#0F3D3E',
              color: '#FFFFFF',
              height: 40,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 2px 6px rgba(15, 61, 62, 0.25)',
              '&:hover': { bgcolor: '#328284' },
            }}
          >
            {t('contactGoogleRating.addRating') || 'Add Google Rating'}
          </Button>
        </Stack>
      </Box>

      {/* Search & Filters Section */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
        <Grid container spacing={2.5} alignItems="center">
          <Grid item xs={12} sm={6} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('contactGoogleRating.searchPlaceholder') || 'Search by contact name, ID, or review URL...'}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#9CA3AF', mr: 1 }} />,
              }}
              sx={{
                bgcolor: '#FFFFFF',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: '#D1D5DB' },
                  '&:hover fieldset': { borderColor: '#328284' },
                  '&.Mui-focused fieldset': { borderColor: '#328284' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('contactGoogleRating.statusLabel') || 'Status'}</InputLabel>
              <Select
                value={statusFilter}
                label={t('contactGoogleRating.statusLabel') || 'Status'}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{
                  bgcolor: '#FFFFFF',
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#328284' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#328284' },
                }}
              >
                <MenuItem value="all">{t('common.all') || 'All Statuses'}</MenuItem>
                <MenuItem value="active">{t('common.active') || 'Active Only'}</MenuItem>
                <MenuItem value="inactive">{t('common.inactive') || 'Inactive Only'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} display="flex" justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              startIcon={<FilterAltOff />}
              sx={{
                ...clearFilterButtonSx,
                height: 40,
                width: '100%',
                borderRadius: '10px',
                borderColor: '#0F3D3E',
                color: '#0F3D3E',
                '&:hover': {
                  borderColor: '#0A2829',
                  bgcolor: '#EBF4F4',
                },
              }}
            >
              {t('common.clearFilters') || 'Clear Filters'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
        <TableContainer>
          <Table sx={{ minWidth: 700 }} aria-label="google ratings table">
            <TableHead sx={{ bgcolor: '#F3F4F6' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('contactGoogleRating.colContact') || 'Title'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('contactGoogleRating.colRating') || 'Google Rating'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('contactGoogleRating.colTotalRatings') || 'Total Reviews'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('contactGoogleRating.colReviewUrl') || 'Review URL'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('contactGoogleRating.colStatus') || 'Status'}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#374151' }}>
                  {t('common.actions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} sx={{ color: '#328284' }} />
                    <Typography variant="body2" sx={{ color: '#6B7280', mt: 1.5 }}>
                      {t('contactGoogleRating.loading') || 'Loading Google ratings...'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredRatings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <StarIcon sx={{ fontSize: 48, color: '#D1D5DB', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                      {t('contactGoogleRating.noDataTitle') || 'No Google Ratings Found'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#9CA3AF', mt: 0.5 }}>
                      {t('contactGoogleRating.noDataSubtitle') || 'Click "Add Google Rating" to configure a rating for a contact.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRatings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const contactTitle = getContactTitle(row.contactId, row);
                    return (
                      <TableRow key={row.contactId || index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        {/* Title (Contact Title) */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                            {contactTitle}
                          </Typography>
                        </TableCell>

                        {/* Rating (Stars + Number) */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Rating value={Number(row.rating) || 0} precision={0.1} readOnly size="small" />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F3D3E' }}>
                              {(Number(row.rating) || 0).toFixed(1)}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Total Ratings Count */}
                        <TableCell>
                          <Chip
                            icon={<ThumbUpIcon sx={{ fontSize: '14px !important', color: '#0284C7 !important' }} />}
                            label={(Number(row.totalRatings) || 0).toLocaleString()}
                            size="small"
                            sx={{ bgcolor: '#E0F2FE', color: '#0284C7', fontWeight: 700, fontSize: '0.75rem' }}
                          />
                        </TableCell>

                        {/* Review URL Link */}
                        <TableCell>
                          {row.reviewUrl ? (
                            <Tooltip title={row.reviewUrl}>
                              <Button
                                size="small"
                                component="a"
                                href={row.reviewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                                sx={{ textTransform: 'none', color: '#328284', fontWeight: 600, fontSize: '0.8rem', p: 0 }}
                              >
                                {row.reviewUrl.length > 35 ? `${row.reviewUrl.substring(0, 35)}...` : row.reviewUrl}
                              </Button>
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Status Chip */}
                        <TableCell>
                          <Chip
                            icon={row.isActive ? <CheckCircleIcon sx={{ fontSize: '14px !important', color: '#16A34A !important' }} /> : <CancelIcon sx={{ fontSize: '14px !important', color: '#6B7280 !important' }} />}
                            label={row.isActive ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                            size="small"
                            sx={{
                              bgcolor: row.isActive ? '#DCFCE7' : '#F3F4F6',
                              color: row.isActive ? '#15803D' : '#4B5563',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                            }}
                          />
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Tooltip title={t('common.edit') || 'Edit Rating'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(row)}
                              sx={{ color: '#328284', '&:hover': { bgcolor: 'rgba(50, 130, 132, 0.08)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t('common.delete') || 'Delete Rating'}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(row)}
                              sx={{ color: '#EF4444', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredRatings.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add / Edit Dialog Modal (Cascade Selection: City -> Category -> Contact Title) */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0F3D3E', pb: 1 }}>
          {isEditMode
            ? (t('contactGoogleRating.dialogEditTitle') || 'Edit Contact Google Rating')
            : (t('contactGoogleRating.dialogAddTitle') || 'Add Contact Google Rating')}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* City Select Dropdown */}
            {cities.length > 0 && (
              <FormControl fullWidth size="small">
                <InputLabel>City</InputLabel>
                <Select
                  value={selectedCityId}
                  label="City"
                  onChange={(e) => {
                    const nextCity = e.target.value;
                    setSelectedCityId(nextCity);
                    setSelectedCategoryId('');
                    setFormSelectedContact(null);
                    setFormData((prev) => ({ ...prev, contactId: null }));
                    fetchCategoriesForCity(nextCity);
                    fetchContactsForModal(nextCity, '');
                  }}
                  sx={{ borderRadius: '8px' }}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Category Select Dropdown */}
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategoryId}
                label="Category"
                onChange={(e) => {
                  const nextCat = e.target.value;
                  setSelectedCategoryId(nextCat);
                  setFormSelectedContact(null);
                  setFormData((prev) => ({ ...prev, contactId: null }));
                  fetchContactsForModal(selectedCityId, nextCat);
                }}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => {
                  const catTitle =
                    cat.title_EN ||
                    cat.title_en ||
                    cat.title ||
                    cat.Title ||
                    cat.name ||
                    cat.Name ||
                    cat.categoryName ||
                    cat.CategoryName ||
                    cat.title_DE ||
                    `Category #${cat.id}`;
                  return (
                    <MenuItem key={cat.id} value={cat.id}>
                      {catTitle}
                    </MenuItem>
                  );
                })}
              </Select>
              {isFetchingCategories ? (
                <FormHelperText>Loading categories...</FormHelperText>
              ) : null}
            </FormControl>

            {/* Contact Title Autocomplete Picker */}
            <Autocomplete
              options={modalContacts}
              loading={isFetchingContacts}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.title || option.name || option.contactPerson || `Contact #${option.id}`;
              }}
              value={formSelectedContact}
              onChange={(event, newValue) => {
                setFormSelectedContact(newValue);
                setFormData((prev) => ({
                  ...prev,
                  contactId: newValue ? newValue.id : null,
                }));
                if (formErrors.contactId) {
                  setFormErrors((prev) => ({ ...prev, contactId: '' }));
                }
              }}
              ListboxProps={{ style: { maxHeight: 220, overflow: 'auto' } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('contactGoogleRating.fieldContact') || 'Select Contact *'}
                  placeholder="Select contact title..."
                  error={Boolean(formErrors.contactId)}
                  helperText={formErrors.contactId}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetchingContacts ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                />
              )}
            />

            {/* Rating Stars & Value */}
            <Box sx={{ p: 2, bgcolor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#374151', mb: 1 }}>
                {t('contactGoogleRating.fieldRating') || 'Google Rating Score (0.0 - 5.0) *'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Rating
                  value={Number(formData.rating) || 0}
                  precision={0.1}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, rating: newValue }));
                    if (formErrors.rating) setFormErrors((prev) => ({ ...prev, rating: '' }));
                  }}
                  size="large"
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ step: '0.1', min: '0', max: '5' }}
                  value={formData.rating}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    setFormData((prev) => ({ ...prev, rating: isNaN(val) ? '' : val }));
                    if (formErrors.rating) setFormErrors((prev) => ({ ...prev, rating: '' }));
                  }}
                  sx={{ width: 100 }}
                  error={Boolean(formErrors.rating)}
                />
              </Box>
              {formErrors.rating && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {formErrors.rating}
                </FormHelperText>
              )}
            </Box>

            {/* Total Ratings Count */}
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('contactGoogleRating.fieldTotalRatings') || 'Total Reviews Count *'}
              placeholder="e.g. 150"
              value={formData.totalRatings}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                setFormData((prev) => ({ ...prev, totalRatings: isNaN(val) ? '' : val }));
                if (formErrors.totalRatings) setFormErrors((prev) => ({ ...prev, totalRatings: '' }));
              }}
              error={Boolean(formErrors.totalRatings)}
              helperText={formErrors.totalRatings || undefined}
            />

            {/* Review URL */}
            <TextField
              fullWidth
              size="small"
              label={t('contactGoogleRating.fieldReviewUrl') || 'Google Review URL'}
              placeholder="https://maps.google.com/?cid=..."
              value={formData.reviewUrl}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, reviewUrl: e.target.value }));
                if (formErrors.reviewUrl) setFormErrors((prev) => ({ ...prev, reviewUrl: '' }));
              }}
              error={Boolean(formErrors.reviewUrl)}
              helperText={formErrors.reviewUrl || undefined}
            />

            {/* Is Active Switch */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#0F3D3E' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0F3D3E' },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151' }}>
                  {t('contactGoogleRating.fieldIsActive') || 'Is Rating Active'}
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ color: '#6B7280' }}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ bgcolor: '#0F3D3E', color: '#FFFFFF', '&:hover': { bgcolor: '#328284' } }}
          >
            {isSubmitting
              ? (t('common.saving') || 'Saving...')
              : isEditMode
              ? (t('common.update') || 'Update Rating')
              : (t('common.save') || 'Save Rating')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#EF4444' }}>
          {t('contactGoogleRating.confirmDeleteTitle') || 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#374151' }}>
            {t('contactGoogleRating.confirmDeleteText') || 'Are you sure you want to delete the Google rating configuration for'} {' '}
            <strong>{itemToDelete ? getContactTitle(itemToDelete.contactId) : ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: '#6B7280' }}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {isSubmitting ? (t('common.deleting') || 'Deleting...') : (t('common.delete') || 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Toast */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactGoogleRating;
